import moment from 'moment';
import dayjs from 'dayjs';

export const colors = {
  blue: 'rgb(44,172,227)',
  yellow: 'rgb(230,189,33)',
  red: 'rgb(196,39,44)',
  green: 'rgb(99,177,70)',
  purple: 'rgb(157,68,181)',
  orange: 'rgb(238,137,68)',
  grey: 'rgb(235,235,235)',
  teal: 'rgb(109,190,191)',
};

export const defaultColors = [
  'rgb(168,181,215)',
  'rgb(151,210,152)',
  'rgb(226,149,188)',
  'rgb(120,195,134)',
  'rgb(182,169,223)',
  'rgb(208,217,149)',
  'rgb(214,155,217)',
  'rgb(168,198,118)',
  'rgb(228,154,127)',
  'rgb(209,199,118)',
  'rgb(139,174,228)',
  'rgb(208,189,127)',
  'rgb(187,185,109)',
  'rgb(130,210,220)',
  'rgb(179,181,168)',
  'rgb(217,162,163)',
  'rgb(117,196,166)',
  'rgb(212,176,198)',
  'rgb(167,190,134)',
  'rgb(178,181,212)',
  'rgb(206,188,137)',
  'rgb(204,196,225)',
  'rgb(133,189,217)',
  'rgb(215,176,142)',
  'rgb(137,211,202)',
  'rgb(215,176,187)',
  'rgb(186,215,176)',
  'rgb(149,177,205)',
  'rgb(209,214,162)',
  'rgb(167,189,221)',
  'rgb(198,186,137)',
  'rgb(161,204,216)',
  'rgb(219,199,165)',
  'rgb(123,179,187)',
  'rgb(207,174,159)',
  'rgb(159,185,203)',
  'rgb(199,193,157)',
  'rgb(195,179,196)',
  'rgb(160,189,153)',
  'rgb(216,199,209)',
  'rgb(146,186,174)',
  'rgb(196,175,176)',
  'rgb(115,177,225)',
  'rgb(202,214,184)',
  'rgb(186,183,197)',
  'rgb(197,204,216)',
  'rgb(196,193,173)',
  'rgb(162,179,189)',
  'rgb(219,207,196)',
  'rgb(125,217,194)',
  'rgb(214,176,129)',
  'rgb(228,157,144)',
  'rgb(170,184,178)',
  'rgb(193,215,202)',
  'rgb(195,211,213)',
  'rgb(197,201,188)',
];

export const getLast7DaysArray = (date: Record<string, string>) => {
  const dates: string[] = [];
  const fromDate = moment(date.to, 'YYYY/MM/DD').subtract(7, 'days');
  const toDate = moment(date.to, 'YYYY/MM/DD');

  while (fromDate.isSameOrBefore(toDate)) {
    dates.push(fromDate.format('YYYY/MM/DD'));
    fromDate.add(1, 'days');
  }

  return dates;
};

export const getMonthsArray = (date: Record<string, string>) => {
  const months: string[] = [];
  const fromDate = moment(date.from, 'YYYY/MM/DD');
  const toDate = moment(date.to, 'YYYY/MM/DD');

  while (
    fromDate.isBefore(toDate, 'month') ||
    fromDate.isSame(toDate, 'month')
  ) {
    months.push(fromDate.format('YYYY/MM'));
    fromDate.add(1, 'month');
  }

  return months;
};

export const getDatesArray = (date: Record<string, string>) => {
  const dates: string[] = [];
  let fromDate = dayjs(date.from);
  const toDate = dayjs(date.to);

  while (fromDate.isBefore(toDate, 'day') || fromDate.isSame(toDate, 'day')) {
    dates.push(fromDate.format('MMM DD YYYY'));
    fromDate = fromDate.add(1, 'day');
  }

  return dates;
};

export const getWeeksArray = (date: Record<string, string>) => {
  const weeks: string[] = [];
  let currentDate = dayjs(date.from);
  const toDate = dayjs(date.to);

  while (currentDate.isBefore(toDate) || currentDate.isSame(toDate, 'day')) {
    const weekStart = currentDate.format('MMM DD YYYY');

    const currentDayOfWeek = currentDate.day(); // 0 is Sunday, 6 is Saturday
    const daysUntilSaturday = currentDayOfWeek === 6 ? 0 : 6 - currentDayOfWeek;
    let weekEnd = currentDate.add(daysUntilSaturday, 'day');
    if (weekEnd.isAfter(toDate)) {
      weekEnd = toDate;
    }

    const weekLabel = `${weekStart} - ${weekEnd.format('MMM DD YYYY')}`;
    weeks.push(weekLabel);

    currentDate = weekEnd.add(1, 'day');

    if (currentDate.isAfter(toDate)) {
      break;
    }
  }

  return weeks;
};

let tooltipTimeout: ReturnType<typeof setTimeout>;
export const customTooltip = {
  enabled: false,
  external: function (context) {
    // Tooltip Element
    let tooltipEl = document.getElementById('chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<table></table>';
      document.body.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    const tooltipModel = context.tooltip;
    if (tooltipModel.opacity === 0) {
      tooltipTimeout = setTimeout(() => {
        tooltipEl.style.opacity = '0';
      }, 400); // Delay hiding the tooltip
      return;
    }
    clearTimeout(tooltipTimeout); // Prevent hiding when moving to the tooltip

    // Set caret Position
    tooltipEl.classList.remove('above', 'below', 'no-transform');
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    } else {
      tooltipEl.classList.add('no-transform');
    }

    // Set Text
    if (tooltipModel.body) {
      const names = tooltipModel.title || [];
      const meta = tooltipModel.dataPoints[0].raw.meta;

      const createDatasetItems = () => {
        return tooltipModel.dataPoints
          .map((dataPoint, index) => {
            const color =
              tooltipModel.labelColors[index].backgroundColor ||
              tooltipModel.labelColors[index].borderColor;
            const colorSquare = `<span style="background-color:${color}; width: 15px; display:inline">&emsp;&nbsp;</span>`;
            const label = dataPoint.dataset.label ?? dataPoint.label;
            const value = dataPoint.formattedValue;

            return `<div style="font-weight: bold">${colorSquare} ${label}: <span>${value}</span></div>`;
          })
          .join('');
      };

      const createMeta = (meta: { name: string; total: number | string }[]) => {
        if (!meta) return '';
        return meta
          .map((data) => `<span>${data.name} : ${data.total}</span><br>`)
          .join('');
      };

      // Show title only if there is a dataset label
      const titleHtml = () => {
        if (tooltipModel.dataPoints[0].dataset.label) {
          const name =
            names.length > 0 && names[0] && names[0] !== 'undefined'
              ? `<div style="font-weight: bold; margin-bottom: 5px;">${names[0]}</div>`
              : '';

          return name;
        } else {
          return '';
        }
      };

      let innerHtml = `<div>${titleHtml()} ${createDatasetItems()}</div>
				<div>${createMeta(meta)}</div>`;

      let tableRoot = tooltipEl.querySelector('table');
      if (tableRoot) {
        tableRoot.innerHTML = innerHtml;
      }
    }

    const position = context.chart.canvas.getBoundingClientRect();
    const tooltipHeight = tooltipEl.offsetHeight;

    // Calculate available space below and above the data point
    const spaceBelow =
      window.innerHeight - (position.top + tooltipModel.caretY);
    const spaceAbove = position.top + tooltipModel.caretY - window.scrollY;

    // Determine if tooltip should be displayed above or below
    const displayAbove = tooltipHeight > spaceBelow && spaceAbove > spaceBelow;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = '1';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.left = `${(position.left + window.pageXOffset + tooltipModel.caretX) * 0.9}px`; // move tooltip left a bit

    if (displayAbove) {
      tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY - tooltipHeight}px`;
    } else {
      tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY}px`;
    }

    tooltipEl.style.padding = `${tooltipModel.padding}px`;
    tooltipEl.style.pointerEvents = 'auto'; // Allow interactions with the tooltip
    tooltipEl.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    tooltipEl.style.color = 'white';
    tooltipEl.style.borderRadius = '3px';
    tooltipEl.style.whiteSpace = 'normal'; // Allow text wrapping
    tooltipEl.style.maxWidth = '500px';
    tooltipEl.style.maxHeight = '500px';
    tooltipEl.style.overflowY = 'auto';

    // Prevent hiding when hovering on the tooltip itself
    tooltipEl.onmouseover = () => {
      clearTimeout(tooltipTimeout); // Stop hiding while håovering
      if (context?.chart?.canvas?.style) {
        context.chart.canvas.style.pointerEvents = 'none'; // Disable chart interaction
      }
    };

    tooltipEl.onmouseleave = () => {
      tooltipTimeout = setTimeout(() => {
        tooltipEl.style.opacity = '0'; // Delay hiding when leaving the tooltip
      }, 300);
      if (context?.chart?.canvas?.style) {
        context.chart.canvas.style.pointerEvents = 'auto'; // Re-enable chart interaction
      }
    };
  },
};

export const fadeColor = (color: string, opacity: number = 0.3): string => {
  if (!color) return '';

  if (color.startsWith('rgba')) {
    return color.replace(
      /rgba\(.*?,.*?,.*?,.*?\)/,
      `rgba($1,$2,$3,${opacity})`,
    );
  }

  return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
};

export const darkColor = (color: string, amount: number = 0.25): string => {
  if (!color) return '';

  const effectiveAmount = Math.max(0, Math.min(1, amount));
  const multiplier = 1 - effectiveAmount; // If amount is 0.2, multiplier is 0.8 (80% of original lightness)
  const match = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);

  if (!match) {
    return color;
  }

  const newR = Math.max(0, Math.round(parseInt(match[1], 10) * multiplier));
  const newG = Math.max(0, Math.round(parseInt(match[2], 10) * multiplier));
  const newB = Math.max(0, Math.round(parseInt(match[3], 10) * multiplier));

  return `rgb(${newR},${newG},${newB})`;
};
